/**
 * A row of joined buttons, one selected — the look of the filter bar's
 * "All 31 · Ready 0 · …" chips. Used where a page switches between views of
 * the same thing (Result / Supporting rows / Exceptions, an analysis's
 * reports), so switching views and filtering read as the same control.
 */

import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import type { ReactNode } from 'react';

export interface SegmentedOption<T extends string | number> {
	value: T;
	label: ReactNode;
}

export function Segmented<T extends string | number>({
	value,
	options,
	onChange,
	ariaLabel,
}: {
	value: T;
	options: SegmentedOption<T>[];
	onChange: (next: T) => void;
	ariaLabel: string;
}) {
	return (
		<ToggleButtonGroup
			size="small"
			exclusive
			value={value}
			aria-label={ariaLabel}
			/* Clicking the selected button again would clear the choice; a view is
         always showing, so that click does nothing. */
			onChange={(_event, next: T | null) => {
				if (next !== null) onChange(next);
			}}
			sx={{ alignSelf: 'flex-start', flexWrap: 'wrap' }}
		>
			{options.map((option) => (
				<ToggleButton
					key={option.value}
					value={option.value}
					sx={{ textTransform: 'none' }}
				>
					{option.label}
				</ToggleButton>
			))}
		</ToggleButtonGroup>
	);
}
